import type { ReactNode } from "react";
import {
  FILTER_CATEGORIES,
  FILTER_ENERGIES,
  FILTER_MAKES,
  FILTER_TRANSMISSIONS,
} from "../../data/marketplace";
import { eur } from "../../lib/format";
import { label, useT } from "../../i18n";

export interface Filters {
  availableOnly: boolean;
  maxWeekly: number;
  maxDeposit: number;
  energies: string[];
  transmissions: string[];
  categories: string[];
  makes: string[];
}

type ListKey = "energies" | "transmissions" | "categories" | "makes";

interface VehicleFiltersProps {
  filters: Filters;
  /** Slider bounds, from the vehicles on offer. */
  limits: { weekly: number; deposit: number };
  /** Makes to offer; the demo list when not given. */
  makes?: string[];
  onChange: (filters: Filters) => void;
  onReset: () => void;
}

function Section({ title, aside, children }: { title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <div className="border-b border-line-strong py-3">
      <div className="mb-2 flex items-center justify-between text-xs font-medium text-ink">
        {title}
        {aside}
      </div>
      {children}
    </div>
  );
}

export default function VehicleFilters({ filters, limits, makes = FILTER_MAKES, onChange, onReset }: VehicleFiltersProps) {
  const t = useT();
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => onChange({ ...filters, [key]: value });
  const toggle = (key: ListKey, value: string) =>
    set(key, filters[key].includes(value) ? filters[key].filter((v) => v !== value) : [...filters[key], value]);

  /** `labels` translates option values; makes are shown as-is. */
  const checkboxes = (key: ListKey, options: readonly string[], labels: Record<string, string> = {}) => (
    <div className="grid gap-0.5">
      {options.map((option) => (
        <label key={option} className="flex cursor-pointer items-center gap-2 py-1 text-xs text-ink-soft">
          <input
            type="checkbox"
            checked={filters[key].includes(option)}
            onChange={() => toggle(key, option)}
            className="h-3.5 w-3.5 accent-brand"
          />
          {label(labels, option)}
        </label>
      ))}
    </div>
  );

  const slider = (key: "maxWeekly" | "maxDeposit", max: number, step: number) => (
    <input
      type="range"
      min={step}
      max={max}
      step={step}
      value={filters[key]}
      onChange={(e) => set(key, Number(e.target.value))}
      aria-label={key === "maxWeekly" ? t.filters.maxWeekly : t.filters.maxDeposit}
      className="w-full accent-brand"
    />
  );

  return (
    <div>
      <div className="flex items-center justify-between pb-2">
        <b className="text-[13px] font-medium">{t.filters.title}</b>
        <button
          type="button"
          onClick={onReset}
          className="cursor-pointer border-0 bg-transparent p-0 text-xs text-brand hover:text-brand-link"
        >
          {t.filters.reset}
        </button>
      </div>

      <div className="border-b border-line-strong py-3">
        <label className="flex cursor-pointer items-center justify-between text-xs text-ink">
          {t.filters.availableOnly}
          <input
            type="checkbox"
            checked={filters.availableOnly}
            onChange={() => set("availableOnly", !filters.availableOnly)}
            className="h-3.5 w-3.5 accent-brand"
          />
        </label>
      </div>

      <Section
        title={t.filters.maxWeekly}
        aside={<span className="font-normal">{filters.maxWeekly >= limits.weekly ? t.filters.all : eur(filters.maxWeekly)}</span>}
      >
        {slider("maxWeekly", limits.weekly, 10)}
      </Section>

      <Section
        title={t.filters.maxDeposit}
        aside={<span className="font-normal">{filters.maxDeposit >= limits.deposit ? t.filters.all : eur(filters.maxDeposit)}</span>}
      >
        {slider("maxDeposit", limits.deposit, 100)}
      </Section>

      <Section title={t.filters.fuel}>{checkboxes("energies", FILTER_ENERGIES, t.enums.energy)}</Section>
      <Section title={t.filters.transmission}>
        {checkboxes("transmissions", FILTER_TRANSMISSIONS, t.enums.transmission)}
      </Section>
      <Section title={t.filters.category}>{checkboxes("categories", FILTER_CATEGORIES, t.enums.category)}</Section>
      <Section title={t.filters.make}>{checkboxes("makes", makes)}</Section>
    </div>
  );
}
